import React, { Dispatch, SetStateAction, useContext } from "react";

import { makePost } from "@culturehq/client";
import { PlainButton } from "@culturehq/components";

import { AWSLanguage, Subtitle } from "chq";
import { Trans, t } from "app/utils";

import ChqModalConfirm from "components/common/ChqModalConfirm";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";


interface TranscriptionOptionProps {
  language: AWSLanguage;
  onLanguageSelected: (subtitle: Subtitle) => void;
  onSetError: Dispatch<SetStateAction<string | undefined>>;
}

const TranscriptionOption: React.FC<TranscriptionOptionProps> = ({
  language, onLanguageSelected, onSetError
}) => {
  const { data, handleUpdateData } = useContext(CreatomateContext);
  const { story, subtitles } = data;

  if (!story) return <></>;

  const postTranscribe = async (languageAbbreviation: string, label: string) => {
    try {
      await makePost("/video/transcription", {
        storyId: story.id,
        language: languageAbbreviation,
        languageLabel: label,
        draftVideo: false as any
      });
    } catch {
      onSetError(t("Something went wrong when we were generating your captions. Please try again or contact support"));
      handleUpdateData({ pendingSubtitle: undefined });
    }
  };

  const handleTranscribe = async (language: AWSLanguage) => {
    handleUpdateData({ burnSubtitles: false, pendingSubtitle: language, subtitlesOn: true });

    if (language.abbreviation && (language.label || language.transcription)) {
      await postTranscribe(
        language.abbreviation, language.label || language.transcription as string
      );
    }
    const activeSubtitleIndex = subtitles.find(subtitle => subtitle.active);
    if (!activeSubtitleIndex && subtitles.length > 0) {
      handleUpdateData({
        subtitles: subtitles.map((subtitle, index) => (
          index === 0 ? { ...subtitle, active: true } : subtitle
        ))
      });
      onLanguageSelected(subtitles[0]);
    }
  };

  return (
    <li>
      <ChqModalConfirm
        onAccept={() => handleTranscribe(language)}
        trigger={(onTrigger: () => void) => (
          <PlainButton onClick={() => onTrigger()}>
            {language.transcription}
            {" "}
            ({language.abbreviation})
          </PlainButton>
        )}
      >
        <Trans>
          Are you sure this is the language the person in this video is speaking in?
        </Trans>
      </ChqModalConfirm>
    </li>
  );
};

export default TranscriptionOption;
